// src/components/desk/ListingMap.jsx — interactive map for the lineage modal's hero
// map slide (owner request 2026-07-23: "not interactive, its like image — I want to
// zoom in and out"). Leaflet, LAZY-imported so the main bundle doesn't grow — cards
// keep the cheap static osmTileUrl tile; only the modal pays for interactivity.
// Mobile answer (three-views rule): leaflet handles pinch-zoom + touch-drag natively.
import { useEffect, useRef } from 'react'

let _leafletPromise = null
const loadLeaflet = () => (_leafletPromise ??= Promise.all([
  import('leaflet'),
  import('leaflet/dist/leaflet.css'),
]).then(([m]) => m.default || m))

// `onPick` turns the map into the ADDRESS ANSWER (1066, owner 2026-09-12: "add select pin on map
// or write in address as an option"). With it, a click drops the pin where the agent clicked and
// reports the coordinates up; the marker turns into the draggable ✋ form so a mis-click is a drag,
// not a re-click. Without it the map is exactly what it was: a read-only view of a stored pin.
export function ListingMap({ lat, lng, precise, onPick = null }) {
  const elRef = useRef(null)
  const mapRef = useRef(null)
  const markerRef = useRef(null)
  const onPickRef = useRef(onPick)
  onPickRef.current = onPick

  useEffect(() => {
    let cancelled = false
    loadLeaflet().then(L => {
      if (cancelled || !elRef.current || mapRef.current) return
      // zoomAnimation rides requestAnimationFrame, which never fires while the document
      // is hidden (background tab / headless pane) — an animated zoom then silently never
      // completes. Non-animated zoom works everywhere; only skip the animation when
      // mounting hidden (the rare case), keep it for the normal visible mount.
      const map = L.map(elRef.current, { center: [lat, lng], zoom: 15, zoomAnimation: !document.hidden })
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19, attribution: '© OpenStreetMap',
      }).addTo(map)
      // ⚠ pin stays EMOJI on purpose (owner-caught regression 2026-07-20, same rule as
      // the static tile in ListingGallery): red color + size ARE the signal on a busy
      // tile. 📍 precise coords / 📌 approximate geocode — honest about which. Bonus over
      // the static tile: the marker sits at the EXACT lat/lng, not the tile's center.
      const marker = L.marker([lat, lng], {
        draggable: !!onPickRef.current,
        icon: L.divIcon({ className: '', html: `<span style="font-size:28px;line-height:1;filter:drop-shadow(0 1px 1px rgba(0,0,0,.4))">${precise ? '📍' : '📌'}</span>`, iconSize: [28, 28], iconAnchor: [14, 26] }),
      }).addTo(map)
      markerRef.current = marker
      if (onPickRef.current) {
        const report = ll => onPickRef.current?.({ lat: Number(ll.lat.toFixed(6)), lng: Number(ll.lng.toFixed(6)) })
        map.on('click', e => { marker.setLatLng(e.latlng); report(e.latlng) })
        marker.on('dragend', () => report(marker.getLatLng()))
      }
      mapRef.current = map
    })
    return () => {
      cancelled = true
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; markerRef.current = null }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng])

  // stopPropagation: the gallery wrapper advances slides on click — dragging/zooming
  // the map must not flip to the next photo (the overlay → button is the way forward).
  //
  // ⚠️ `isolate` is LOAD-BEARING (owner-reported 2026-07-25, screenshot: map tiles + the
  // "© OpenStreetMap" credit painted over the lineage modal's title bar). Leaflet stamps
  // z-index 400 on `.leaflet-pane` and 800–1000 on its controls/attribution. Those are
  // positioned elements in whatever stacking context contains them — which was the modal's
  // scroll box, where the sticky header carries only z-10 (see Modal in primitives.jsx).
  // 400 > 10, so the map won on scroll. `isolation: isolate` gives this element its own
  // stacking context, trapping Leaflet's z-indices inside it; the element itself then paints
  // at z-auto in DOM order and the sticky header wins.
  // Do NOT "fix" this by raising the header instead — that is a race you lose at 1000.
  return <div ref={elRef} className="w-full h-full relative isolate" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()} />
}
